/** TanStack Query hooks for the AI Consumption Console (P3). */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  aiUsageKeys,
  BUDGET_KEYS,
  getBudget,
  getByFeature,
  getByModel,
  getByUser,
  getToday,
  getTrend,
  putAppConfig,
  type BreakdownResponse,
  type BudgetStatusResponse,
  type ByUserResponse,
  type Granularity,
  type SpendTrendResponse,
  type TodayGaugeResponse,
} from "@/features/admin/ai-usage/api";

const STALE = 30_000;

export function useTrend(window: Granularity, days: number): UseQueryResult<SpendTrendResponse, Error> {
  return useQuery({ queryKey: aiUsageKeys.trend(window, days), queryFn: () => getTrend({ window, days }), staleTime: STALE });
}
export function useByFeature(days: number): UseQueryResult<BreakdownResponse, Error> {
  return useQuery({ queryKey: aiUsageKeys.byFeature(days), queryFn: () => getByFeature(days), staleTime: STALE });
}
export function useByModel(days: number): UseQueryResult<BreakdownResponse, Error> {
  return useQuery({ queryKey: aiUsageKeys.byModel(days), queryFn: () => getByModel(days), staleTime: STALE });
}
export function useByUser(days: number, limit: number): UseQueryResult<ByUserResponse, Error> {
  return useQuery({ queryKey: aiUsageKeys.byUser(days, limit), queryFn: () => getByUser(days, limit), staleTime: STALE });
}
export function useToday(): UseQueryResult<TodayGaugeResponse, Error> {
  return useQuery({ queryKey: aiUsageKeys.today(), queryFn: getToday, staleTime: STALE });
}
export function useBudget(): UseQueryResult<BudgetStatusResponse, Error> {
  return useQuery({ queryKey: aiUsageKeys.budget(), queryFn: getBudget, staleTime: STALE });
}

/** Form values for the budget config form (all strings — the form input shape). */
export interface BudgetFormValues {
  dailyUsd: string;
  monthlyUsd: string;
  dailyTokens: string;
  monthlyTokens: string;
  alertPct: string;
}

export function useSaveBudget(): UseMutationResult<void, Error, BudgetFormValues> {
  const qc = useQueryClient();
  return useMutation<void, Error, BudgetFormValues>({
    mutationFn: async (v) => {
      await Promise.all([
        putAppConfig(BUDGET_KEYS.dailyUsd, v.dailyUsd, "float"),
        putAppConfig(BUDGET_KEYS.monthlyUsd, v.monthlyUsd, "float"),
        putAppConfig(BUDGET_KEYS.dailyTokens, v.dailyTokens, "int"),
        putAppConfig(BUDGET_KEYS.monthlyTokens, v.monthlyTokens, "int"),
        putAppConfig(BUDGET_KEYS.alertPct, v.alertPct, "int"),
      ]);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: aiUsageKeys.budget() });
    },
  });
}
